'use client'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Image from '@/components/ui/MediaImage'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext"
import BrochureDownloadModal from "@/components/ui/BrochureDownloadModal"
import RequestForm from "@/components/ui/RequestForm"

import 'swiper/css'
import 'swiper/css/navigation'
import type { PageUrlMap } from "@/types/blog";
import { buildProductDetailUrl, resolveProductTypeKeyForCategoryUrl } from "@/lib/url-helpers";
import { formatCurrencyAmount, type CurrencyForFormat } from "@/lib/formatCurrency";
import type { CalculatedPrice } from "@/types/product-search";
import { parseTourDurationMeta } from '@/lib/tour-duration';

/** Kart başına gösterilecek maksimum resim (performans) */
const MAX_IMAGES_PER_CARD = 10;

/** Aynı parametrelerle tour-departure-dates/prices tekrar istenmesin (Strict Mode / re-render dedupe) */
const departurePricesCache = new Map<
    string,
    Promise<{ success?: boolean; data?: Array<{ departure_date?: string; calculated_price?: { success?: boolean; price_per_person?: number | null; final_price?: number } }> }>
>()

export interface TourRouteItem {
    location_type?: string; // 'origin' | 'waypoints' | 'destination'
    location_title?: string;
    location_description?: string;
    location_address?: string; // Türkiye adreslerinde virgülden önceki kısım il/ilçe olabilir
}

export interface PricingPeriod {
    period_name?: string;
    /** API tour_pricing: private | group | daily */
    periodType?: string;
    period_type?: string;
    period_start?: string; // Date string
    period_end?: string; // Date string
    periodStart?: string;
    periodEnd?: string;
    quota?: number;
    packages?: Array<{
        package_name?: string;
        prices?: Array<{
            pax_range_id?: number;
            price_per_person?: number;
            single_supplement?: number;
            extra_bed_price?: number;
        }>;
    }>;
    /** Ürün detay / tour_pricing satır formatı */
    rows?: Array<{
        paxRange?: string;
        maxPax?: number;
        max_pax?: number;
        perPersonDouble?: number;
        price_per_person?: number;
    }>;
}

function periodTypeOfCard(p: PricingPeriod): string {
    return String(p.periodType ?? p.period_type ?? '').toLowerCase().trim()
}

function dateInPricingPeriod(ymd: string, start?: string, end?: string): boolean {
    const d = ymd.slice(0, 10)
    const s = (start || '').slice(0, 10)
    const e = (end || '').slice(0, 10)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s) && d < s) return false
    if (/^\d{4}-\d{2}-\d{2}$/.test(e) && d > e) return false
    return true
}

function parseMaxPaxFromTourCardRow(row: NonNullable<PricingPeriod['rows']>[0]): number {
    const m = row.maxPax ?? row.max_pax
    if (typeof m === 'number' && !Number.isNaN(m)) return m
    const label = row.paxRange || ''
    const range = /(\d+)\s*[-–]\s*(\d+)/i.exec(label)
    if (range) return Math.max(parseInt(range[1], 10), parseInt(range[2], 10))
    const plus = /(\d+)\s*\+/i.exec(label)
    if (plus) return parseInt(plus[1], 10)
    const single = /^(\d+)\s*$/i.exec(label.trim())
    if (single) return parseInt(single[1], 10)
    return 0
}

function rowPerPersonTourCard(row: NonNullable<PricingPeriod['rows']>[0]): number | null {
    const a = row.price_per_person ?? row.perPersonDouble
    if (a == null || Number.isNaN(Number(a))) return null
    const n = Number(a)
    return n > 0 ? n : null
}

function marketingPerPersonFromPricingRows(rows: NonNullable<PricingPeriod['rows']>): number | null {
    if (!rows?.length) return null
    let bestPp: number | null = null
    let bestMax = -1
    for (const row of rows) {
        const pp = rowPerPersonTourCard(row)
        if (pp == null) continue
        const maxP = parseMaxPaxFromTourCardRow(row)
        if (maxP > bestMax) {
            bestMax = maxP
            bestPp = pp
        } else if (maxP === bestMax && bestPp != null) {
            bestPp = Math.min(bestPp, pp)
        }
    }
    return bestPp
}

/** Liste kartı: private/daily dönemlerinde kişi başı “from”; tarih seçiliyse dönem kesişimi. */
function getTourMarketingPerPersonForKind(
    pricingPeriods: PricingPeriod[] | undefined,
    kind: 'private' | 'daily',
    departureYmd?: string
): number | null {
    if (!pricingPeriods?.length) return null
    const kindPeriods = pricingPeriods.filter((p) => periodTypeOfCard(p) === kind)
    if (kindPeriods.length === 0) return null
    const narrowed =
        departureYmd && /^\d{4}-\d{2}-\d{2}$/.test(departureYmd)
            ? kindPeriods.filter((p) =>
                  dateInPricingPeriod(departureYmd, p.periodStart ?? p.period_start, p.periodEnd ?? p.period_end)
              )
            : kindPeriods
    const use = narrowed.length > 0 ? narrowed : kindPeriods
    const candidates: number[] = []
    for (const period of use) {
        if (period.packages && period.packages.length > 0) {
            for (const pkg of period.packages) {
                for (const pr of pkg.prices || []) {
                    const pp = pr.price_per_person
                    if (pp != null && pp > 0) candidates.push(pp)
                }
            }
        } else if (period.rows && period.rows.length > 0) {
            const m = marketingPerPersonFromPricingRows(period.rows)
            if (m != null) candidates.push(m)
        }
    }
    if (candidates.length === 0) return null
    return Math.min(...candidates)
}

export interface GroupDepartureDateItem {
    period_id?: number | null
    departure_date?: string
    quota?: number | null
    status?: string
    discount_percentage?: number | null
}

export interface TourProps {
    src: string[];
    alt: string[];
    type: string;
    imageCount: number;
    videoCount: number;
    title: string;
    transferType: string;
    start: string;
    stops: string;
    end: string;
    tourDuration: string;
    tourType?: string; // Group veya Private (ProductTypeField'dan tour_type)
    tag: string;
    price: PriceProps;
    calculated_price?: CalculatedPrice; // YENİ: Hesaplanmış fiyat (kampanya dahil)
    base_price?: number; // En düşük fiyat (tarih ve kişi sayısı yoksa)
    currency?: {
        id: number;
        code: string;
        symbol: string;
        symbol_position?: string;
        thousands_separator?: string;
        decimal_separator?: string;
    } | null; // Para birimi (base_price için)
    reviews?: number;
    tourId?: string;
    productId?: string; // Display/gösterim (örn. EN-136)
    /** Broşür indirme API'si için veritabanı product.id (sayı) */
    brochureProductId?: number;
    slug?: string;
    url?: string;
    is_featured?: boolean; // Öne çıkarılmış tur mu?
    // YENİ ALANLAR
    transfer?: string[]; // field_transfer[] - Transfer tipleri (uçak, otobüs vb.)
    tourDurationDay?: number; // field_tour_duration[day]
    tourDurationNight?: number; // field_tour_duration[night]
    tourRoute?: TourRouteItem[]; // tour_route - Tur güzergahı
    pricingPeriods?: PricingPeriod[]; // tour_pricing - Fiyatlandırma dönemleri
    groupDepartureDates?: GroupDepartureDateItem[]; // Liste kartı için çıkış tarihleri
    /** Ürün tipi / meta (liste API); rezervasyon CTA için */
    booking_flow?: 'request_first' | 'instant_online' | 'bank_transfer_only';
}
export interface PriceProps {
    discount_price: string;
    price: string;
}
export interface TourCardProps {
    tour: TourProps;
    viewType?: 'box' | 'horizontal';
    index?: number;
    languageId?: number;
    language?: string;
    languageCode?: string;
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    /** Liste sayfası segmenti (group-tours / private-tours). Verilirse link bu base ile oluşturulur; yoksa tour.type kullanılır. */
    productTypeForUrl?: string;
    /** MainSearchBar'daki çıkış tarihi (YYYY-MM-DD) – seçili kriterlere göre fiyat göstermek için */
    searchDepartureDate?: string;
    /** MainSearchBar'daki yetişkin sayısı */
    searchPaxCount?: number;
    /** MainSearchBar'daki çocuk sayısı */
    searchChildCount?: number;
    /**
     * true (varsayılan): liste kartlarında /api/tour-departure-dates/prices yalnızca kart görününce.
     * false: hemen istek (özel senaryolar).
     */
    lazyDeparturePriceFetch?: boolean;
}

/** Resim sütununu Link veya div olarak sarmalar. Bileşen dışında tanımlı olmalı; her render'da yeni referans oluşmaz, sonsuz güncelleme döngüsü önlenir. */
function TourCardImageColumn({
    isLink,
    href,
    className,
    children,
}: {
    isLink: boolean
    href: string
    className: string
    children: React.ReactNode
}) {
    if (isLink) {
        return (
            <Link href={href} className={className}>
                {children}
            </Link>
        )
    }
    return <div className={className}>{children}</div>
}

// URL normalize fonksiyonu - buildProductDetailUrl kullanarak doğru base URL'i alır
const normalizeProductUrl = (
    url: string | undefined,
    productType: string,
    productSlug: string | undefined,
    pageUrls?: PageUrlMap,
    language?: string,
    defaultLanguage: string = 'en'
): string => {
    const listingType = resolveProductTypeKeyForCategoryUrl(productType, undefined, 'private-tours');

    const isLegacyTourPath = (path: string) =>
        /^(?:\/[a-z]{2})?\/(?:tour(?:\/|$)|tour-detail(?:\/|$))/.test(path);

    if (productSlug && pageUrls && language) {
        const correctUrl = buildProductDetailUrl(
            listingType,
            productSlug,
            pageUrls,
            language,
            defaultLanguage,
        );

        if (url) {
            const normalizedUrl = url.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
            if (isLegacyTourPath(normalizedUrl)) {
                return correctUrl;
            }
            if (normalizedUrl === correctUrl || normalizedUrl.startsWith(correctUrl.split('/').slice(0, -1).join('/'))) {
                return normalizedUrl;
            }
        }

        return correctUrl;
    }

    if (url) {
        const normalizedUrl = url.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
        if (normalizedUrl !== '/' && normalizedUrl !== '#' && !isLegacyTourPath(normalizedUrl)) {
            return normalizedUrl;
        }
    }

    return '#';
};

export default function TourCard({ 
    tour, 
    viewType = 'box', 
    index = 0,
    languageId,
    language,
    languageCode,
    pageUrls,
    defaultLanguage = 'en',
    productTypeForUrl,
    searchDepartureDate,
    searchPaxCount = 0,
    searchChildCount = 0,
    lazyDeparturePriceFetch = true,
}: TourCardProps) {
    const { getVariable } = useLanguageVariables()
    const [swiperTInstance, setSwiperTInstance] = useState<SwiperType | null>(null)
    const [isHovered, setIsHovered] = useState(false)
    const [brochureModalOpen, setBrochureModalOpen] = useState(false)
    const [requestFormModalOpen, setRequestFormModalOpen] = useState(false)
    /** MainSearchBar kriterlerine göre hesaplanan fiyat (seçili tarih + kişi sayısı) */
    const [searchContextPrice, setSearchContextPrice] = useState<{ pricePerPerson: number; currency: { symbol?: string; code?: string }; total?: number } | null>(null)
    const [showRouteTooltip, setShowRouteTooltip] = useState(false)
    const [showPricingTooltip, setShowPricingTooltip] = useState(false)
    const [showDepartureTooltip, setShowDepartureTooltip] = useState(false)
    const [imagesLoaded, setImagesLoaded] = useState<Record<number, boolean>>({})
    const routeTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pricingTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const departureTooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const cardRootRef = useRef<HTMLDivElement>(null)
    const [departurePriceFetchAllowed, setDeparturePriceFetchAllowed] = useState(!lazyDeparturePriceFetch)

    const rootInViewProps = { animate: 'visible' as const }

    // Language code'u belirle
    const resolvedLanguageCode = language || languageCode || (typeof window !== 'undefined' ? (() => {
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const possibleLang = pathParts[0];
        if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
            return possibleLang;
        }
        const cookieLang = document.cookie
            .split('; ')
            .find(row => row.startsWith('language='))
            ?.split('=')[1];
        return cookieLang || 'en';
    })() : 'en');

    const isTurkish = (resolvedLanguageCode || '').toLowerCase() === 'tr'

    const formatTransferItem = (raw: string): string => {
        const key = (raw ?? '').toString().trim().toLowerCase()

        const transferMap: Record<string, string> = {
            vehicle: getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
            airplane: getVariable('transfer_airplane', isTurkish ? 'Uçakla' : 'Airplane'),
            ferry: getVariable('transfer_ferry', isTurkish ? 'Feribotla' : 'Ferry'),
            train: getVariable('transfer_train', isTurkish ? 'Trenle' : 'Train'),
            ship: getVariable('transfer_ship', isTurkish ? 'Gemiyle' : 'Ship'),
            yacht: getVariable('transfer_yacht', isTurkish ? 'Yatla' : 'Yacht'),
            // Geriye dönük uyumluluk / farklı kaynak formatları
            car: getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
            bus: getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
            minibus: getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
            private_vip_transfer: getVariable('transfer_private_vip', isTurkish ? 'Özel VIP Transfer' : 'Private VIP Transfer'),
            // Bazı eski/veri kaynakları doğrudan TR değer döndürebiliyor
            'araç': getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
            'arac': getVariable('transfer_vehicle', isTurkish ? 'Araç' : 'Vehicle'),
        }

        return transferMap[key] || raw
    }

    const formatTransferText = (transfer: string[]): string => {
        return (transfer || []).map(formatTransferItem).join(', ')
    }

    // Product URL'ini oluştur: liste sayfası segmenti (group-tours/private-tours) varsa onu kullan, yoksa tour.type
    const effectiveProductType = productTypeForUrl ?? tour.type;
    const productUrl = normalizeProductUrl(
        tour.url,
        effectiveProductType,
        tour.slug,
        pageUrls,
        resolvedLanguageCode,
        defaultLanguage
    );

    // TODO: Broşür İndirme Özelliği - Geliştirilecek
    // Detaylar: docs/BROCHURE_DOWNLOAD_PLAN.md
    // const handleDownloadBrochure = () => {
    //     // Brochure indirme işlemi
    // }

    const formatPrice = (amount: number, currency: CalculatedPrice['currency']) =>
        formatCurrencyAmount(amount, currency as CurrencyForFormat);

    const formatDepartureDate = (dateStr?: string): string => {
        if (!dateStr) return ''
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return dateStr
        return date.toLocaleDateString(isTurkish ? 'tr-TR' : 'en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    }

    // MainSearchBar'daki tarih + kişi sayısına göre fiyat: liste API'si zaten calculate_prices + departure_date + pax_count ile çağrıldığı için
    // ürünlerde calculated_price varsa ek API isteği atma (tour-departure-dates/prices her kart için tekrarlanmasın).
    const productIdNum = tour.brochureProductId ?? (tour.productId && /^\d+$/.test(String(tour.productId)) ? Number(tour.productId) : null)

    const needsLazyDeparturePriceBridge = useMemo(() => {
        if (!lazyDeparturePriceFetch) return false
        if (!searchDepartureDate || !/^\d{4}-\d{2}-\d{2}$/.test(searchDepartureDate) || searchPaxCount < 1) return false
        const calc = tour.calculated_price
        const fromList = calc?.success && (calc.final_price ?? 0) > 0 && (calc.price_per_person ?? 0) > 0
        if (fromList) return false
        return productIdNum != null
    }, [lazyDeparturePriceFetch, searchDepartureDate, searchPaxCount, tour.calculated_price, productIdNum])

    useEffect(() => {
        if (!needsLazyDeparturePriceBridge) {
            setDeparturePriceFetchAllowed(true)
            return
        }
        setDeparturePriceFetchAllowed(false)
        const el = cardRootRef.current
        if (!el || typeof IntersectionObserver === 'undefined') {
            setDeparturePriceFetchAllowed(true)
            return
        }
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setDeparturePriceFetchAllowed(true)
                    obs.disconnect()
                }
            },
            { root: null, rootMargin: '48px 0px', threshold: 0.05 },
        )
        obs.observe(el)
        return () => obs.disconnect()
    }, [needsLazyDeparturePriceBridge])

    useEffect(() => {
        if (!searchDepartureDate || !/^\d{4}-\d{2}-\d{2}$/.test(searchDepartureDate) || searchPaxCount < 1) {
            setSearchContextPrice(null)
            return
        }
        const calc = tour.calculated_price
        const fromList = calc?.success && (calc.final_price ?? 0) > 0 && (calc.price_per_person ?? 0) > 0
        if (fromList && calc) {
            setSearchContextPrice({
                pricePerPerson: Number(calc.price_per_person),
                currency: { symbol: tour.currency?.symbol ?? '€', code: tour.currency?.code },
                total: calc.final_price ?? undefined,
            })
            return
        }
        if (productIdNum == null) {
            setSearchContextPrice(null)
            return
        }
        if (!departurePriceFetchAllowed) {
            return
        }
        const month = searchDepartureDate.slice(0, 7)
        const params = new URLSearchParams({
            product_id: String(productIdNum),
            month,
            pax_count: String(searchPaxCount),
            child_count: String(searchChildCount || 0),
            language: resolvedLanguageCode || 'en',
        })
        const cacheKey = `prices:${productIdNum}:${month}:${searchPaxCount}:${searchChildCount ?? 0}:${resolvedLanguageCode ?? 'en'}`
        let cancelled = false
        const fetchPromise =
            departurePricesCache.get(cacheKey) ??
            (() => {
                const p = fetch(`/api/tour-departure-dates/prices?${params.toString()}`).then((res) => res.json())
                departurePricesCache.set(cacheKey, p)
                return p
            })()
        fetchPromise
            .then((data: { success?: boolean; data?: Array<{ departure_date?: string; calculated_price?: { success?: boolean; price_per_person?: number | null; final_price?: number } }> }) => {
                if (cancelled || !data?.success || !Array.isArray(data.data)) return
                const row = data.data.find((d) => (d.departure_date ?? '').slice(0, 10) === searchDepartureDate)
                const rowCalc = row?.calculated_price
                const pricePerPerson = rowCalc?.success && rowCalc?.price_per_person != null ? Number(rowCalc.price_per_person) : null
                const finalPrice = rowCalc?.success && rowCalc?.final_price != null ? Number(rowCalc.final_price) : null
                if (pricePerPerson != null && pricePerPerson > 0) {
                    const symbol = tour.currency?.symbol ?? '€'
                    setSearchContextPrice({
                        pricePerPerson,
                        currency: { symbol, code: tour.currency?.code },
                        total: finalPrice != null && finalPrice > 0 ? finalPrice : undefined,
                    })
                } else {
                    setSearchContextPrice(null)
                }
            })
            .catch(() => {
                if (!cancelled) setSearchContextPrice(null)
            })
        return () => {
            cancelled = true
        }
    }, [searchDepartureDate, searchPaxCount, searchChildCount, productIdNum, resolvedLanguageCode, tour.currency?.symbol, tour.currency?.code, tour.calculated_price, departurePriceFetchAllowed])

    // Rota için kısa konum gösterimi: tam adres yerine son 2 parça (şehir/ülke gibi)
    const getCompactRouteLocation = (route: TourRouteItem): string => {
        const address = (route.location_address || '').trim()
        if (address) {
            const parts = address.split(',').map(p => p.trim()).filter(Boolean)
            if (parts.length >= 2) {
                return parts.slice(-2).join(', ')
            }
            if (parts.length === 1) {
                return parts[0]
            }
        }
        return (route.location_title || '').trim()
    }

    // Fiyat: hesaplanmış fiyat varsa onu göster; yoksa base_price (en düşük kişi başı) ile "From €X" göster
    const hasCalculatedPrice = tour.calculated_price?.success && (tour.calculated_price.final_price ?? 0) > 0;
    const hasBasePriceOnly = !hasCalculatedPrice && (tour.base_price != null && tour.base_price > 0) && tour.currency;

    const hasGroupPrice = Boolean(tour.groupDepartureDates && tour.groupDepartureDates.length > 0);
    const hasPrivatePrice = Boolean(tour.pricingPeriods && tour.pricingPeriods.length > 0);
    const hasPrivateOnlyPrice = !hasGroupPrice && hasPrivatePrice;
    const hasBothPrices = hasGroupPrice && hasPrivatePrice;
    const hasDailyTourPeriod = Boolean(tour.pricingPeriods?.some((p) => periodTypeOfCard(p) === 'daily'));
    const hasPrivateKindPeriod = Boolean(tour.pricingPeriods?.some((p) => periodTypeOfCard(p) === 'private'));
    const bestPrivatePrice = React.useMemo(() => {
        if (!tour.currency) return null;
        const fromPrivate = getTourMarketingPerPersonForKind(tour.pricingPeriods, 'private', searchDepartureDate);
        const fromDaily = getTourMarketingPerPersonForKind(tour.pricingPeriods, 'daily', searchDepartureDate);
        const structured = [fromPrivate, fromDaily].filter((x): x is number => x != null && x > 0);
        if (structured.length > 0) {
            return Math.min(...structured);
        }
        if (!tour.pricingPeriods?.length) return null;
        let minPrice: number | null = null;
        for (const period of tour.pricingPeriods) {
            for (const pkg of period.packages || []) {
                for (const p of pkg.prices || []) {
                    const pp = p.price_per_person;
                    if (pp != null && pp > 0 && (minPrice == null || pp < minPrice)) minPrice = pp;
                }
            }
        }
        return minPrice;
    }, [tour.pricingPeriods, tour.currency, searchDepartureDate]);

    // Tur kodu etiketi - dil çevirisi (Tur Kodu / Tour code) - productId badge'lerinde kullanılır
    const tourCodeLabel = getVariable('tour_detail.product_id', 'Tour code');
    // Tur tipi rozeti: veriden türet (grup/özel dönem varlığına göre)
    const tourTypeBadgeLabel = hasBothPrices
        ? (getVariable('tour_card_type_group_private', 'Group & Private') as string)
        : hasGroupPrice
            ? (getVariable('tour_card_type_group', 'Group Tour') as string)
            : hasDailyTourPeriod && hasPrivateKindPeriod
                ? (getVariable('tour_card_type_private_daily', 'Private & Daily') as string)
                : hasDailyTourPeriod
                    ? (getVariable('tour_card_type_daily', 'Daily tour') as string)
                    : hasPrivatePrice
                        ? (getVariable('tour_card_type_private', 'Private Tour') as string)
                        : tour.tourType || null;

    const imageSources = Array.isArray(tour.src) ? tour.src.slice(0, MAX_IMAGES_PER_CARD) : [];
    const imageColClass = `relative overflow-hidden h-[250px] max-w-full ${viewType == 'box' ? 'w-full rounded-tl-lg rounded-tr-lg' : 'w-4/12 shrink-0 rounded-lg'}`;

    const openRouteTooltip = () => {
        if (routeTooltipHideTimeoutRef.current) {
            clearTimeout(routeTooltipHideTimeoutRef.current)
            routeTooltipHideTimeoutRef.current = null
        }
        setShowRouteTooltip(true)
    }

    const closeRouteTooltipWithDelay = () => {
        if (routeTooltipHideTimeoutRef.current) {
            clearTimeout(routeTooltipHideTimeoutRef.current)
        }
        routeTooltipHideTimeoutRef.current = setTimeout(() => {
            setShowRouteTooltip(false)
            routeTooltipHideTimeoutRef.current = null
        }, 180)
    }

    const openDepartureTooltip = () => {
        if (departureTooltipHideTimeoutRef.current) {
            clearTimeout(departureTooltipHideTimeoutRef.current)
            departureTooltipHideTimeoutRef.current = null
        }
        setShowDepartureTooltip(true)
    }

    const closeDepartureTooltipWithDelay = () => {
        if (departureTooltipHideTimeoutRef.current) {
            clearTimeout(departureTooltipHideTimeoutRef.current)
        }
        departureTooltipHideTimeoutRef.current = setTimeout(() => {
            setShowDepartureTooltip(false)
            departureTooltipHideTimeoutRef.current = null
        }, 180)
    }

    const openPricingTooltip = () => {
        if (pricingTooltipHideTimeoutRef.current) {
            clearTimeout(pricingTooltipHideTimeoutRef.current)
            pricingTooltipHideTimeoutRef.current = null
        }
        setShowPricingTooltip(true)
    }

    const closePricingTooltipWithDelay = () => {
        if (pricingTooltipHideTimeoutRef.current) {
            clearTimeout(pricingTooltipHideTimeoutRef.current)
        }
        pricingTooltipHideTimeoutRef.current = setTimeout(() => {
            setShowPricingTooltip(false)
            pricingTooltipHideTimeoutRef.current = null
        }, 180)
    }

    return (
            <>
            <div
                ref={cardRootRef}
                className={`relative font-sans flex group h-full ${hasCalculatedPrice ? 'cursor-pointer' : ''} ${tour.is_featured ? 'shadow-tour-card-featured rounded-lg' : 'shadow-tour-card'} ${viewType == 'box' ? `flex-col rounded-tr-lg` : `flex-row bg-white rounded-lg p-4`}`}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <TourCardImageColumn
                    isLink={true}
                    href={productUrl}
                    className={imageColClass}
                >
                    {/* Loading skeleton - resimler yüklenirken göster */}
                    {!imagesLoaded[0] && (
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse z-0" />
                    )}
                    <Swiper
                        modules={[Navigation]}
                        spaceBetween={0}
                        slidesPerView={1}
                        /* Nested carousel’da loop ölçüleri ~2^25 px’e şişirip anasayfa kartlarını patlatıyordu */
                        nested
                        watchOverflow
                        observer
                        observeParents
                        loop={false}
                        onSwiper={(swiper) => {
                            setSwiperTInstance(swiper)
                            requestAnimationFrame(() => {
                                try {
                                    swiper.update()
                                } catch {
                                    /* ignore */
                                }
                            })
                        }}
                        className="!w-full !max-w-full h-full overflow-hidden"
                    >
                        {imageSources.map((image, imgIndex) => {
                            const cardIndex = index ?? 0;
                            /** Dış Swiper’da çok kart varken yalnız ilk slaydın ilk görseli öncelikli; diğerleri lazy */
                            const isPriorityImage = false; // Cards can be below the fold; visible images load through ViewportImage.
                            return (
                            <SwiperSlide key={imgIndex} className="!w-full !max-w-full">
                                <div className="relative w-full h-full max-w-full">
                                    {!imagesLoaded[imgIndex] && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse z-10" />
                                    )}
                                    <Image
                                        src={image}
                                        alt={tour.alt[imgIndex] || tour.title}
                                        fill
                                        className={`object-cover h-[250px] transition-opacity duration-300 ${viewType == 'box' ? ' rounded-tl-lg rounded-tr-lg': ' rounded-lg'} ${imagesLoaded[imgIndex] ? 'opacity-100' : 'opacity-0'}`}
                                        sizes={viewType === 'box' ? "(min-width: 1536px) 25vw, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" : "33vw"}
                                        priority={isPriorityImage}
                                        loading={isPriorityImage ? undefined : 'lazy'}
                                        decoding="async"
                                        unoptimized={image.startsWith('/api/media/proxy/') || image.includes('unsplash.com') || image.startsWith('http://') || image.startsWith('https://')}
                                        onLoad={() => {
                                            setImagesLoaded(prev => ({ ...prev, [imgIndex]: true }))
                                        }}
                                        onError={() => {
                                            // Hata durumunda da loaded olarak işaretle (skeleton'ı kaldırmak için)
                                            setImagesLoaded(prev => ({ ...prev, [imgIndex]: true }))
                                        }}
                                    />
                                </div>
                            </SwiperSlide>
                        );
                        })}
                    </Swiper>
                    {/* Box: broşür hâlâ görsel üzerinde; tur kodu metin alanında */}
                    {viewType === 'box' && (tour.brochureProductId ?? tour.productId) && (
                        <div className="absolute top-3 right-3 z-20 flex flex-col gap-2 items-end">
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBrochureModalOpen(true); }}
                                className="flex flex-row gap-2 items-center bg-alsero-first text-white px-3 py-2 rounded-lg shadow-lg font-medium text-sm transition-all"
                            >
                                <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.76496 8.78578C5.90889 8.94597 6.11319 9.03651 6.3291 9.03651C6.54501 9.03651 6.74931 8.94597 6.89325 8.78578L10.2712 5.04104C10.4731 4.81817 10.5219 4.49779 10.4012 4.22384C10.2805 3.94989 10.0065 3.77345 9.70702 3.77345H8.11209V0.755378C8.11209 0.335169 7.77314 -0.00378418 7.35293 -0.00378418H5.30296C4.88275 -0.00378418 4.5438 0.335169 4.5438 0.755378V3.77345H2.94886C2.64937 3.77345 2.37775 3.94989 2.2547 4.22384C2.13398 4.49779 2.18273 4.81817 2.38471 5.04104L5.76496 8.78578Z" fill="currentColor"/>
                                    <path d="M11.9345 6.78687C11.5816 6.78687 11.2938 7.07242 11.2938 7.42763V9.57742C11.2938 9.85369 11.0709 10.0766 10.7946 10.0766H1.85647C1.5802 10.0766 1.35732 9.85369 1.35732 9.57742V7.42763C1.35732 7.07474 1.07177 6.78687 0.718885 6.78687C0.366003 6.78687 0.078125 7.07242 0.078125 7.42763V9.57742C0.078125 10.5595 0.874432 11.3558 1.85647 11.3558H10.7923C11.7743 11.3558 12.5706 10.5595 12.5706 9.57742V7.42763C12.5706 7.07474 12.2851 6.78687 11.9322 6.78687H11.9345Z" fill="currentColor"/>
                                </svg>
                                <span>{getVariable('tour_card_download_brochure', 'Download Brochure')}</span>
                            </button>
                        </div>
                    )}
                    {viewType === 'box' ?
                        (
                        <div
                            className={`w-full h-[250px] absolute top-0 z-10 transition-opacity duration-300 ${isHovered ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                        >
                            <div className="flex flex-col justify-between h-full">
                                <div className="w-full flex flex-col gap-2 h-full items-end p-4 pt-4 font-medium text-sm text-white">
                                    <div className="flex flex-row gap-2">
                                        {(tour.videoCount !== undefined && tour.videoCount !== null && Number(tour.videoCount) > 0) && (
                                            <div
                                                className="flex flex-row gap-2 items-center bg-black/70 px-3 rounded-lg py-2 backdrop-blur-md hover:bg-black/80 transition-all duration-300">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M2.02342 3.3408H0.703125V12.5829C0.703125 13.3091 1.29726 13.9032 2.02342 13.9032H11.2655V12.5829H2.02342V3.3408ZM12.5858 0.700195H4.66402C3.93786 0.700195 3.34372 1.29433 3.34372 2.0205V9.94229C3.34372 10.6685 3.93786 11.2626 4.66402 11.2626H12.5858C13.312 11.2626 13.9061 10.6685 13.9061 9.94229V2.0205C13.9061 1.29433 13.312 0.700195 12.5858 0.700195ZM7.30462 8.95207V3.01072L11.2655 5.98139L7.30462 8.95207Z" fill="white"/>
                                                </svg>
                                                <span>{tour.videoCount}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="flex flex-row gap-2 justify-end mb-10 me-4">
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            swiperTInstance?.slidePrev();
                                        }}
                                        className="group p-3 border border-white rounded-lg ring-1 ring-black/10 flex items-center cursor-pointer transition-all ease-in-out duration-300 hover:bg-alsero-dark-gray relative z-20"
                                        aria-label="Previous image"
                                        type="button"
                                    >
                                        <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-white" aria-hidden="true" focusable="false">
                                            <path d="M0.863846 6.09914L4.75299 10.2732C4.86401 10.3924 5.01198 10.4578 5.16975 10.4578C5.3277 10.4578 5.47557 10.3923 5.58659 10.2732L5.9397 9.89415C6.05063 9.77519 6.11174 9.6163 6.11174 9.44687C6.11174 9.27755 6.05063 9.11329 5.9397 8.99433L3.67083 6.55393L10.8791 6.55393C11.2041 6.55394 11.4609 6.28087 11.4609 5.93197L11.4609 5.39608C11.4609 5.04719 11.2041 4.74659 10.8791 4.74659L3.64508 4.74659L5.93961 2.29256C6.05054 2.17342 6.11165 2.01884 6.11165 1.84942C6.11165 1.68019 6.05054 1.52336 5.93961 1.4043L5.58651 1.02646C5.47549 0.907314 5.32761 0.842383 5.16966 0.842383C5.01189 0.842383 4.86392 0.908159 4.75291 1.02731L0.863758 5.20129C0.752477 5.32082 0.691277 5.48037 0.691715 5.64998C0.691365 5.82015 0.752477 5.9798 0.863846 6.09914Z"/>
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            swiperTInstance?.slideNext();
                                        }}
                                        className="group p-3 border border-white rounded-lg ring-1 ring-black/10 flex items-center cursor-pointer transition-all ease-in-out duration-300 hover:bg-alsero-dark-gray relative z-20"
                                        aria-label="Next image"
                                        type="button"
                                    >
                                        <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-white" aria-hidden="true" focusable="false">
                                            <path d="M11.1362 4.9702L7.24701 0.796123C7.13599 0.676974 6.98802 0.611573 6.83025 0.611573C6.6723 0.611573 6.52443 0.677068 6.41341 0.796123L6.0603 1.17518C5.94937 1.29414 5.88826 1.45304 5.88826 1.62246C5.88826 1.79179 5.94937 1.95604 6.0603 2.075L8.32917 4.5154L1.12086 4.5154C0.795858 4.5154 0.539062 4.78847 0.539062 5.13736L0.539062 5.67325C0.539062 6.02215 0.795857 6.32275 1.12086 6.32275L8.35491 6.32275L6.06039 8.77677C5.94946 8.89592 5.88834 9.0505 5.88834 9.21992C5.88834 9.38915 5.94946 9.54598 6.06039 9.66504L6.41349 10.0429C6.52451 10.162 6.67239 10.227 6.83034 10.227C6.98811 10.227 7.13608 10.1612 7.24709 10.042L11.1362 5.86805C11.2475 5.74852 11.3087 5.58897 11.3083 5.41936C11.3086 5.24919 11.2475 5.08954 11.1362 4.9702Z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        ) :
                        (
                            <div className="absolute top-0 p-3 z-10 h-full w-full ">
                                <div className="flex flex-col items-start justify-between w-full h-full">
                                    <div className="flex flex-col gap-2 items-start">
                                        {tour.is_featured && (
                                            <span 
                                                className={`bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-lg px-3 py-1.5 flex items-center gap-1.5 shadow-lg font-semibold text-xs`}
                                            >
                                                <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7 0L8.5716 5.14286L14 5.14286L9.7142 8.28571L11.2858 13.4286L7 10.2857L2.7142 13.4286L4.2858 8.28571L0 5.14286L5.4284 5.14286L7 0Z" fill="white"/>
                                                </svg>
                                                {getVariable('tour_card_featured', 'Featured')}
                                            </span>
                                        )}
                                    </div>
    
                                    <div className="flex flex-row gap-2 self-end
                                    [&>button]:border [&>button]:border-white [&>button]:ring-1 [&>button]:ring-black/10 [&>button]:hover:border-alsero-dark-gray [&>button]:hover:bg-alsero-dark-gray
                                    [&>button]:[&>svg]:fill-white
                                    ">
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                swiperTInstance?.slidePrev();
                                            }}
                                            className="group p-3 rounded-sm flex items-center cursor-pointer transition-all ease-in-out duration-300 relative z-20"
                                            aria-label="Previous image"
                                            type="button"
                                        >
                                            <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                                <path d="M0.863846 6.09938L4.75299 10.2735C4.86401 10.3926 5.01198 10.458 5.16975 10.458C5.3277 10.458 5.47557 10.3925 5.58659 10.2735L5.9397 9.8944C6.05063 9.77544 6.11174 9.61654 6.11174 9.44712C6.11174 9.27779 6.05063 9.11354 5.9397 8.99458L3.67083 6.55418L10.8791 6.55418C11.2041 6.55418 11.4609 6.28111 11.4609 5.93222L11.4609 5.39633C11.4609 5.04743 11.2041 4.74683 10.8791 4.74683L3.64508 4.74683L5.93961 2.29281C6.05054 2.17366 6.11165 2.01908 6.11165 1.84966C6.11165 1.68043 6.05054 1.5236 5.93961 1.40454L5.58651 1.02671C5.47549 0.907558 5.32761 0.842627 5.16966 0.842627C5.01189 0.842627 4.86392 0.908403 4.75291 1.02755L0.863758 5.20153C0.752477 5.32106 0.691277 5.48061 0.691715 5.65022C0.691365 5.8204 0.752477 5.98004 0.863846 6.09938Z"/>
                                            </svg>
    
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                swiperTInstance?.slideNext();
                                            }}
                                            className="group p-3 rounded-sm flex items-center cursor-pointer transition-all ease-in-out duration-300  relative z-20"
                                            aria-label="Next image"
                                            type="button"
                                        >
                                            <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                                                <path d="M11.1362 4.97093L7.24701 0.796856C7.13599 0.677706 6.98802 0.612306 6.83025 0.612306C6.6723 0.612306 6.52443 0.6778 6.41341 0.796855L6.0603 1.17592C5.94937 1.29488 5.88826 1.45377 5.88826 1.62319C5.88826 1.79252 5.94937 1.95678 6.0603 2.07574L8.32917 4.51613L1.12086 4.51613C0.795858 4.51613 0.539062 4.7892 0.539062 5.1381L0.539062 5.67399C0.539062 6.02288 0.795857 6.32348 1.12086 6.32348L8.35491 6.32348L6.06039 8.7775C5.94946 8.89665 5.88834 9.05123 5.88834 9.22065C5.88834 9.38988 5.94946 9.54671 6.06039 9.66577L6.41349 10.0436C6.52451 10.1628 6.67239 10.2277 6.83034 10.2277C6.98811 10.2277 7.13608 10.1619 7.24709 10.0428L11.1362 5.86878C11.2475 5.74925 11.3087 5.5897 11.3083 5.42009C11.3086 5.24992 11.2475 5.09027 11.1362 4.97093Z"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </TourCardImageColumn>
                <div className={'flex-1 flex flex-col min-h-0 w-full ' + (hasCalculatedPrice ? 'relative z-20' : '')}>
                    <div
                        className={
                            viewType === 'box'
                                ? 'relative flex flex-1 flex-col justify-between min-h-0 px-5 pt-8 pb-5 bg-white shadow-tour-card -mt-1 group-hover:shadow-2xl z-50 transition-shadow duration-500 rounded-lg text-start items-start'
                                : 'w-full h-full py-2.5 px-6 flex flex-col justify-between'
                        }
                    >
                        
                        {/* Featured Badge - Öne çıkarılmış turlar için */}
                        {viewType == 'box' && tour.is_featured && (
                            <span 
                                className={`absolute left-4 -top-[15px] bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-lg px-4 py-2 flex items-center gap-1.5 shadow-lg hover:shadow-xl transition-all duration-300 z-10 font-semibold text-sm`}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 0L8.5716 5.14286L14 5.14286L9.7142 8.28571L11.2858 13.4286L7 10.2857L2.7142 13.4286L4.2858 8.28571L0 5.14286L5.4284 5.14286L7 0Z" fill="white"/>
                                </svg>
                                {getVariable('tour_card_featured', 'Featured')}
                            </span>
                        )}
                        {/* Tur kodu (box): grid kartında slider üstünde değil, başlığın üstünde */}
                        {viewType === 'box' && tour.productId && (
                            <span
                                className="inline-flex items-center bg-alsero-yellow text-alsero-text-dark px-3 py-1.5 rounded-lg shadow font-semibold text-xs mb-2"
                                title={`${tourCodeLabel}: ${tour.productId}`}
                                aria-label={`${tourCodeLabel}: ${tour.productId}`}
                            >
                                {tourCodeLabel} {tour.productId}
                            </span>
                        )}
                        {/* Tag - Kategori veya tur tipi göster (sadece productId yoksa) */}
                        {viewType == 'box' && !tour.productId && tour.tag && !tour.is_featured && (
                            <span className={`absolute right-4 -top-[15px] bg-gradient-to-r from-alsero-first to-alsero-first/90 text-white rounded-lg px-8 py-3 flex items-center shadow-lg hover:shadow-xl transition-all duration-300`}>
                                {tour.tag}
                            </span>
                        )}
                        {/* Başlık; horizontal'da sağda tur kodu + Download Brochure (slider üstünde değil) */}
                        <div className={`flex flex-row justify-between items-start gap-3 ${viewType === 'box' ? 'mb-2' : ''}`}>
                            <Link href={productUrl} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-alsero-first/50 rounded min-w-0 flex-1">
                                <h2
                                    className={`${viewType === 'box' ? 'text-xl font-bold min-h-[55px]' : 'text-lg font-bold mb-2'} text-start text-alsero-second line-clamp-2 hover:text-alsero-first transition-colors`}
                                >
                                    {tour.title}
                                </h2>
                            </Link>
                            {viewType === 'horizontal' && (tour.productId || (tour.brochureProductId ?? tour.productId)) && (
                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                    {tour.productId && (
                                        <span
                                            className="flex items-center bg-alsero-yellow text-alsero-text-dark px-3 py-1.5 rounded-lg shadow font-semibold text-xs"
                                            title={`${tourCodeLabel}: ${tour.productId}`}
                                            aria-label={`${tourCodeLabel}: ${tour.productId}`}
                                        >
                                            {tourCodeLabel} {tour.productId}
                                        </span>
                                    )}
                                    {(tour.brochureProductId ?? tour.productId) ? (
                                        <button
                                            type="button"
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBrochureModalOpen(true); }}
                                            className="flex flex-row gap-2 items-center bg-alsero-first cursor-pointer text-white px-3 py-2 rounded-lg shadow-lg font-medium text-sm transition-all"
                                        >
                                            <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.76496 8.78578C5.90889 8.94597 6.11319 9.03651 6.3291 9.03651C6.54501 9.03651 6.74931 8.94597 6.89325 8.78578L10.2712 5.04104C10.4731 4.81817 10.5219 4.49779 10.4012 4.22384C10.2805 3.94989 10.0065 3.77345 9.70702 3.77345H8.11209V0.755378C8.11209 0.335169 7.77314 -0.00378418 7.35293 -0.00378418H5.30296C4.88275 -0.00378418 4.5438 0.335169 4.5438 0.755378V3.77345H2.94886C2.64937 3.77345 2.37775 3.94989 2.2547 4.22384C2.13398 4.49779 2.18273 4.81817 2.38471 5.04104L5.76496 8.78578Z" fill="currentColor"/>
                                                <path d="M11.9345 6.78687C11.5816 6.78687 11.2938 7.07242 11.2938 7.42763V9.57742C11.2938 9.85369 11.0709 10.0766 10.7946 10.0766H1.85647C1.5802 10.0766 1.35732 9.85369 1.35732 9.57742V7.42763C1.35732 7.07474 1.07177 6.78687 0.718885 6.78687C0.366003 6.78687 0.078125 7.07242 0.078125 7.42763V9.57742C0.078125 10.5595 0.874432 11.3558 1.85647 11.3558H10.7923C11.7743 11.3558 12.5706 10.5595 12.5706 9.57742V7.42763C12.5706 7.07474 12.2851 6.78687 11.9322 6.78687H11.9345Z" fill="currentColor"/>
                                            </svg>
                                            <span>{getVariable('tour_card_download_brochure', 'Download Brochure')}</span>
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                                
                        <div
                            className="space-y-3 flex flex-col">
                            
                            <div className={`flex flex-col gap-3 ${viewType == 'box' ? 'gap-2 items-start' : 'justify-between items-start'}`}>
                                <div className={`text-sm text-alsero-light-gray ${viewType == 'box' ? 'flex flex-wrap items-start gap-3' : 'flex items-center gap-3 flex-wrap'}`}>
                                    {/* Tur tipi rozeti: Grup Turu / Özel Tur / Grup & Özel */}
                                    {tourTypeBadgeLabel && (
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-alsero-first/10 text-alsero-first border border-alsero-first/20 px-2.5 py-1 text-xs font-semibold">
                                            {tourTypeBadgeLabel}
                                        </span>
                                    )}
                                    {/* Tour Duration - day ve night değerlerini göster */}
                                    {(tour.tourDurationDay !== undefined || tour.tourDurationNight !== undefined || tour.tourDuration) && (
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-md border border-alsero-first/15 bg-alsero-first/[0.07] px-2.5 py-1 text-xs font-semibold text-alsero-second sm:text-sm"
                                            title={tour.tourDuration || undefined}
                                        >
                                            <svg className="h-4 w-4 shrink-0 text-alsero-first" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v4l3.5 2.1.5-.8-3-1.8V5z" fill="currentColor"/>
                                            </svg>
                                            <span className="whitespace-nowrap">
                                            {(() => {
                                                const parsed = parseTourDurationMeta(
                                                    tour.tourDurationDay !== undefined || tour.tourDurationNight !== undefined
                                                        ? { day: tour.tourDurationDay ?? 0, night: tour.tourDurationNight ?? 0 }
                                                        : tour.tourDuration,
                                                );
                                                if (parsed.label) {
                                                    return parsed.label;
                                                }
                                                const parts: string[] = [];
                                                if (tour.tourDurationNight !== undefined && tour.tourDurationNight > 0) {
                                                    parts.push(`${tour.tourDurationNight} ${tour.tourDurationNight === 1 ? getVariable('tour_card_night', 'Gece') : getVariable('tour_card_nights', 'Gece')}`);
                                                }
                                                if (tour.tourDurationDay !== undefined && tour.tourDurationDay > 0) {
                                                    parts.push(`${tour.tourDurationDay} ${tour.tourDurationDay === 1 ? getVariable('tour_card_day', 'Gün') : getVariable('tour_card_days', 'Gün')}`);
                                                }
                                                return parts.length > 0 ? parts.join(' ') : '';
                                            })()}
                                            </span>
                                        </span>
                                    )}
                                    {/* Meta tur tipi (örn. daily-tour): rozet zaten gösteriyorsa tekrar gösterme — çift satır önlenir */}
                                    {tour.tourType && !tourTypeBadgeLabel && (
                                        <span className="flex items-center gap-1">
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                                            </svg>
                                            {(() => {
                                                const raw = String(tour.tourType).trim().toLowerCase()
                                                if (raw === 'daily-tour' || raw === 'daily') return getVariable('tour_card_type_daily', 'Daily tour')
                                                if (raw === 'private-tour' || raw === 'private') return getVariable('tour_card_type_private', 'Private Tour')
                                                if (raw === 'group-tour' || raw === 'group') return getVariable('tour_card_type_group', 'Group Tour')
                                                return tour.tourType
                                            })()}
                                        </span>
                                    )}
                                    {/* Reviews - "(X Review)" formatında */}
                                    {tour.reviews !== undefined && tour.reviews > 0 && (
                                        <span>
                                            ({tour.reviews} {getVariable('tour_card_review', tour.reviews === 1 ? 'Review' : 'Reviews')})
                                        </span>
                                    )}
                                </div>
                                {/* Transfer - field_transfer[] array'inden göster */}
                                {viewType === 'horizontal' && (tour.transfer && tour.transfer.length > 0) && (
                                    <span className="text-sm text-alsero-light-gray flex items-center gap-1">
                                       
                                        {(() => {
                                            const rawLabel = getVariable('tour_card_transfer_round_trip', 'Transport')
                                            if (rawLabel.trim().toLowerCase() === 'round trip') return 'Transport'
                                            return rawLabel
                                        })()}: {formatTransferText(tour.transfer)}
                                    </span>
                                )}
                                
                                {/* Transfer - box view'da göster */}
                                {viewType == 'box' && (tour.transfer && tour.transfer.length > 0) && (
                                    <span className="text-sm text-alsero-light-gray flex items-center gap-1">
                                       
                                        {(() => {
                                            const rawLabel = getVariable('tour_card_transfer_round_trip', 'Transport')
                                            if (rawLabel.trim().toLowerCase() === 'round trip') return 'Transport'
                                            return rawLabel
                                        })()}: {formatTransferText(tour.transfer)}
                                    </span>
                                )}
                                {/* Transfer ve Tooltip'ler - Transfer'in yanında göster */}
                                <div className="flex flex-col items-start gap-3 flex-wrap">
                                    
                                     {/* Start - End (Başlangıç - Bitiş) - konum ikonu ile */}
                                     {(tour.start || tour.end) && (
                                        <span className="flex items-center gap-1">
                                            <svg width="16" height="16" viewBox="0 0 23 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0" aria-hidden>
                                                <path d="M11.5 0C8.45107 0.00344861 5.528 1.21616 3.37208 3.37208C1.21616 5.528 0.00344861 8.45107 0 11.5C0 17.651 10.5362 30.5507 10.9847 31.0969C11.0473 31.173 11.126 31.2344 11.215 31.2765C11.3041 31.3186 11.4015 31.3404 11.5 31.3404C11.5985 31.3404 11.6959 31.3186 11.785 31.2765C11.8741 31.2344 11.9527 31.173 12.0153 31.0969C12.4638 30.5507 23 17.651 23 11.5C22.9966 8.45107 21.7838 5.528 19.6279 3.37208C17.472 1.21616 14.5489 0.00344861 11.5 0ZM11.5 29.6078C9.36475 26.9128 1.33364 16.4468 1.33364 11.5C1.33364 10.1649 1.5966 8.84295 2.1075 7.60951C2.61841 6.37607 3.36726 5.25534 4.31129 4.3113C5.25533 3.36727 6.37606 2.61842 7.6095 2.10751C8.84294 1.59661 10.1649 1.33365 11.5 1.33365C12.8351 1.33365 14.1571 1.59661 15.3905 2.10751C16.6239 2.61842 17.7447 3.36727 18.6887 4.3113C19.6327 5.25534 20.3816 6.37607 20.8925 7.60951C21.4034 8.84295 21.6664 10.1649 21.6664 11.5C21.6664 16.4468 13.6353 26.9128 11.5 29.6078Z" fill="currentColor"/>
                                                <path d="M11.4999 6.42285C10.5717 6.42285 9.66428 6.69811 8.89248 7.21381C8.12067 7.72952 7.51912 8.46251 7.1639 9.32009C6.80867 10.1777 6.71573 11.1213 6.89682 12.0317C7.07791 12.9422 7.52491 13.7784 8.18127 14.4348C8.83764 15.0912 9.6739 15.5381 10.5843 15.7192C11.4947 15.9003 12.4384 15.8074 13.296 15.4522C14.1536 15.0969 14.8865 14.4954 15.4022 13.7236C15.918 12.9518 16.1932 12.0444 16.1932 11.1161C16.1918 9.87184 15.6969 8.67891 14.817 7.79906C13.9371 6.91921 12.7442 6.42428 11.4999 6.42285ZM11.4999 14.4758C10.8354 14.4758 10.1859 14.2787 9.6334 13.9096C9.08091 13.5404 8.6503 13.0157 8.39602 12.4018C8.14173 11.7879 8.0752 11.1124 8.20483 10.4607C8.33446 9.80899 8.65444 9.21036 9.1243 8.7405C9.59415 8.27065 10.1928 7.95067 10.8445 7.82104C11.4962 7.69141 12.1717 7.75794 12.7856 8.01222C13.3995 8.26651 13.9242 8.69712 14.2934 9.24961C14.6625 9.8021 14.8596 10.4517 14.8596 11.1161C14.8585 12.0069 14.5043 12.8608 13.8744 13.4906C13.2446 14.1205 12.3906 14.4748 11.4999 14.4758Z" fill="currentColor"/>
                                            </svg>
                                            <span 
                                            className="text-sm text-alsero-light-gray flex flex-wrap items-center gap-x-2 gap-y-0.5 hover:text-alsero-first transition-colors cursor-pointer"
                                            >
                                                {(() => {
                                                    const startLabel = getVariable('tour_card_start_label', 'Start')
                                                    const endLabel = getVariable('tour_card_end_label', 'End')
                                                    const parts: string[] = []
                                                    if (tour.start) parts.push(`${startLabel} : ${tour.start}`)
                                                    if (tour.end) parts.push(`${endLabel} : ${tour.end}`)
                                                    return parts.join(' · ')
                                                })()}
                                            </span>
                                        </span>
                                    )}

                                    {/* Tur Güzergahı Tooltip - Transfer'in yanında */}
                                    {tour.tourRoute && tour.tourRoute.length > 0 && (
                                        <div className="relative inline-block">
                                            <button
                                                type="button"
                                                className="text-sm text-alsero-light-gray flex items-center gap-1 hover:text-alsero-first transition-colors cursor-pointer"
                                                onMouseEnter={openRouteTooltip}
                                                onMouseLeave={closeRouteTooltipWithDelay}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    setShowRouteTooltip(!showRouteTooltip)
                                                }}
                                            >
                                            
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                                                    <path d="M4.82655 9.78592H14.7534C16.9447 9.78592 18.7233 8.06692 18.7233 5.95643C18.7233 3.84594 16.9405 2.12693 14.7534 2.12693H5.06908C4.82655 0.697257 3.43943 -0.25162 2.00975 0.0590076C0.728989 0.335569 -0.13903 1.56101 0.0184179 2.85881C0.197128 4.31401 1.55022 5.30968 2.97989 5.07142C4.05215 4.88844 4.89037 4.05022 5.07335 2.97796H14.7577C16.4767 2.97796 17.8766 4.31401 17.8766 5.95647C17.8766 7.59888 16.4767 8.93498 14.7577 8.93498H4.82655C2.63524 8.93498 0.85237 10.654 0.85237 12.7645C0.85237 14.875 2.6352 16.594 4.82228 16.594H11.7366L9.91551 18.4194C9.74529 18.581 9.74106 18.8534 9.90701 19.0193C10.073 19.1853 10.341 19.1938 10.507 19.0278C10.5112 19.0235 10.5155 19.0193 10.5155 19.0193L13.0642 16.4706C13.2301 16.3046 13.2301 16.0366 13.0642 15.8706L10.5155 13.3176C10.3452 13.156 10.0772 13.1602 9.91551 13.3261C9.75806 13.4921 9.75806 13.7516 9.91551 13.9176L11.7409 15.743H4.82655C3.10328 15.743 1.7034 14.4069 1.7034 12.7645C1.7034 11.122 3.10328 9.78592 4.82655 9.78592ZM2.55438 4.25019C1.61404 4.25019 0.85237 3.48856 0.85237 2.54818C0.85237 1.60784 1.614 0.846168 2.55438 0.846168C3.49472 0.846168 4.25639 1.6078 4.25639 2.54818C4.25639 3.48852 3.49472 4.25019 2.55438 4.25019Z" fill="black"/>
                                                    <path d="M17.447 13.6113C16.0386 13.6113 14.894 14.7559 14.894 16.1643C14.894 17.5727 16.0386 18.7173 17.447 18.7173C18.8555 18.7173 20 17.5727 20 16.1643C20 14.7559 18.8597 13.6156 17.447 13.6113ZM17.447 17.8663C16.5067 17.8663 15.745 17.1047 15.745 16.1643C15.745 15.2239 16.5067 14.4623 17.447 14.4623C18.3874 14.4623 19.149 15.2239 19.149 16.1643C19.149 17.1047 18.3874 17.8663 17.447 17.8663Z" fill="black"/>
                                                </svg>
                                                <span className="block text-left leading-5">
                                                    {(() => {
                                                        const label = getVariable('tour_card_route', 'Tur Güzergahı')
                                                        const routeItems = (tour.tourRoute || [])
                                                            .map((r) => getCompactRouteLocation(r))
                                                            .filter(Boolean)
                                                        if (routeItems.length === 0) return label
                                                        const preview = routeItems.slice(0, 2).join(' - ')
                                                        const remaining = routeItems.length - 2
                                                        return `${label}: ${preview}${remaining > 0 ? ` +${remaining}` : ''}`
                                                    })()}
                                                </span>
                                            </button>
                                            {showRouteTooltip && (
                                                <div 
                                                    className="absolute left-0 bottom-full mb-2 z-[100] bg-white rounded-xl shadow-tour-card p-4 min-w-[300px] max-w-[500px] max-h-[400px] overflow-y-auto"
                                                    onMouseEnter={openRouteTooltip}
                                                    onMouseLeave={closeRouteTooltipWithDelay}
                                                >
                                                    <h4 className="font-semibold text-alsero-second mb-3 text-base">{getVariable('tour_card_route', 'Tur Güzergahı')}</h4>
                                                    <div className="space-y-2">
                                                        {tour.tourRoute.map((route, idx) => {
                                                            const location = getCompactRouteLocation(route)
                                                            const label = location || route.location_title || ''
                                                            return (
                                                                <div key={idx} className="flex items-start gap-2 text-sm py-1">
                                                                    <span className="font-medium text-alsero-first shrink-0">{idx + 1}.</span>
                                                                    <span className="text-alsero-dark-gray">{label}</span>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* Fiyatlandırma Dönemleri Tooltip - Transfer'in yanında */}
                                    {tour.pricingPeriods && tour.pricingPeriods.length > 0 && (
                                        <div className="relative inline-block">
                                            <button
                                                type="button"
                                                className="text-sm text-alsero-light-gray flex items-center gap-1 hover:text-alsero-first transition-colors cursor-pointer"
                                                onMouseEnter={openPricingTooltip}
                                                onMouseLeave={closePricingTooltipWithDelay}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    e.stopPropagation()
                                                    setShowPricingTooltip(!showPricingTooltip)
                                                }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 2H4c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 10H4V6h8v6zM5 8h6v1H5V8zm0 2h4v1H5v-1z" fill="currentColor"/>
                                                </svg>
                                                <span>{getVariable('tour_card_other_dates', 'Diğer tur tarihleri')} ({tour.pricingPeriods.length})</span>
                                            </button>
                                            {showPricingTooltip && (
                                                <div 
                                                    className="absolute left-0 bottom-full mb-2 z-[100] bg-white rounded-lg shadow-2xl border-2 border-gray-300 p-4 min-w-[300px] max-w-[400px] max-h-[400px] overflow-y-auto"
                                                    onMouseEnter={openPricingTooltip}
                                                    onMouseLeave={closePricingTooltipWithDelay}
                                                >
                                                    <h4 className="font-semibold text-alsero-second mb-3 text-base">{getVariable('tour_card_pricing_periods', 'Fiyatlandırma Dönemleri')}</h4>
                                                    <div className="space-y-3">
                                                        {tour.pricingPeriods.map((period, idx) => {
                                                            const formatDate = (dateStr?: string): string => {
                                                                if (!dateStr) return ''
                                                                try {
                                                                    const date = new Date(dateStr)
                                                                    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                } catch {
                                                                    return dateStr
                                                                }
                                                            }
                                                            return (
                                                                <div key={idx} className="border-b border-gray-100 pb-2 last:border-0">
                                                                    <div className="font-medium text-alsero-first">{period.period_name || `${getVariable('tour_card_period', 'Dönem')} ${idx + 1}`}</div>
                                                                    {period.period_start && period.period_end && (
                                                                        <div className="text-sm text-alsero-light-gray">
                                                                            {formatDate(period.period_start)} - {formatDate(period.period_end)}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* Box: departure dates tour route'un hemen altında */}
                                {viewType === 'box' && tour.groupDepartureDates && tour.groupDepartureDates.length > 0 && (
                                    <div className="relative inline-block mt-2">
                                        <button
                                            type="button"
                                            className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-3 w-full text-left hover:border-alsero-first/40 hover:bg-neutral-50 transition-colors cursor-pointer"
                                            onMouseEnter={openDepartureTooltip}
                                            onMouseLeave={closeDepartureTooltipWithDelay}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                setShowDepartureTooltip(!showDepartureTooltip)
                                            }}
                                        >
                                            <div className="text-xs font-semibold uppercase tracking-wide text-alsero-dark-gray/80 mb-1.5">
                                                {getVariable('tour_card_departure_dates', 'Departure dates')}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-sm text-alsero-dark-gray flex-wrap">
                                                {(() => {
                                                    const sorted = [...tour.groupDepartureDates].sort((a, b) => {
                                                        const dA = a.departure_date ? new Date(a.departure_date).getTime() : 0
                                                        const dB = b.departure_date ? new Date(b.departure_date).getTime() : 0
                                                        return dA - dB
                                                    })
                                                    const previewCount = Math.min(4, sorted.length)
                                                    const preview = sorted.slice(0, previewCount)
                                                    const remaining = sorted.length - previewCount
                                                    const previewText = preview.map((d) => formatDepartureDate(d.departure_date)).filter(Boolean).join(', ')
                                                    return (
                                                        <>
                                                            <span>{previewText}</span>
                                                            {remaining > 0 && (
                                                                <span className="text-alsero-first font-medium">+{remaining}</span>
                                                            )}
                                                        </>
                                                    )
                                                })()}
                                            </div>
                                        </button>
                                        {showDepartureTooltip && (
                                            <div
                                                className="absolute left-0 bottom-full mb-2 z-[100] bg-white rounded-xl shadow-tour-card p-4 min-w-[280px] max-w-[400px] max-h-[320px] overflow-y-auto"
                                                onMouseEnter={openDepartureTooltip}
                                                onMouseLeave={closeDepartureTooltipWithDelay}
                                            >
                                                <h4 className="font-semibold text-alsero-second mb-3 text-base">
                                                    {getVariable('tour_card_departure_dates', 'Departure dates')}
                                                </h4>
                                                <div className="space-y-2">
                                                    {[...tour.groupDepartureDates]
                                                        .sort((a, b) => {
                                                            const dA = a.departure_date ? new Date(a.departure_date).getTime() : 0
                                                            const dB = b.departure_date ? new Date(b.departure_date).getTime() : 0
                                                            return dA - dB
                                                        })
                                                        .map((dep, idx) => {
                                                            const quota = dep.quota ?? null
                                                            const showLastSeats = quota !== null && quota > 0 && quota <= 5
                                                            return (
                                                                <div key={`box-dep-${dep.departure_date ?? idx}-${idx}`} className="flex items-center justify-between gap-2 py-1 text-sm">
                                                                    <span className="text-alsero-dark-gray">{formatDepartureDate(dep.departure_date)}</span>
                                                                    {showLastSeats && (
                                                                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 shrink-0">
                                                                            {getVariable('tour_detail.last_seats', 'Last 5 seats')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div
                            className={`${viewType == 'box' ? 'mt-4' : 'mt-2'} w-full flex flex-col pt-3 border-t border-alsero-light-gray/20 ${viewType === 'box' ? '-mx-5 px-5 pb-0' : ''}`}
                        >
                            <div className={`w-full flex items-end justify-start gap-4 ${(viewType === 'horizontal' || viewType === 'box') && tour.productId ? 'flex-row' : 'flex-col'}`}>
                                <div className={`w-full min-w-0 flex flex-row items-center justify-between gap-2 flex-wrap`}>
                                {viewType !== 'box' && tour.groupDepartureDates && tour.groupDepartureDates.length > 0 && (
                                    <div className="relative inline-block min-w-0 flex-1 max-w-[50%]">
                                        <button
                                            type="button"
                                            className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-2 w-full text-left hover:border-alsero-first/40 hover:bg-neutral-50 transition-colors cursor-pointer"
                                            onMouseEnter={openDepartureTooltip}
                                            onMouseLeave={closeDepartureTooltipWithDelay}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                setShowDepartureTooltip(!showDepartureTooltip)
                                            }}
                                        >
                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-alsero-dark-gray/80 mb-0.5">
                                                {getVariable('tour_card_departure_dates', 'Departure dates')}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-xs text-alsero-dark-gray flex-nowrap overflow-hidden">
                                                {(() => {
                                                    const sorted = [...tour.groupDepartureDates].sort((a, b) => {
                                                        const dA = a.departure_date ? new Date(a.departure_date).getTime() : 0
                                                        const dB = b.departure_date ? new Date(b.departure_date).getTime() : 0
                                                        return dA - dB
                                                    })
                                                    const previewCount = Math.min(2, sorted.length)
                                                    const preview = sorted.slice(0, previewCount)
                                                    const remaining = sorted.length - previewCount
                                                    const previewText = preview.map((d) => formatDepartureDate(d.departure_date)).filter(Boolean).join(', ')
                                                    return (
                                                        <>
                                                            <span className="truncate">{previewText}</span>
                                                            {remaining > 0 && (
                                                                <span className="text-alsero-first font-medium shrink-0">+{remaining}</span>
                                                            )}
                                                        </>
                                                    )
                                                })()}
                                            </div>
                                        </button>
                                        {showDepartureTooltip && (
                                            <div
                                                className="absolute left-0 bottom-full mb-2 z-[100] bg-white rounded-xl shadow-tour-card p-4 min-w-[280px] max-w-[400px] max-h-[320px] overflow-y-auto"
                                                onMouseEnter={openDepartureTooltip}
                                                onMouseLeave={closeDepartureTooltipWithDelay}
                                            >
                                                <h4 className="font-semibold text-alsero-second mb-3 text-base">
                                                    {getVariable('tour_card_departure_dates', 'Departure dates')}
                                                </h4>
                                                <div className="space-y-2">
                                                    {[...tour.groupDepartureDates]
                                                        .sort((a, b) => {
                                                            const dA = a.departure_date ? new Date(a.departure_date).getTime() : 0
                                                            const dB = b.departure_date ? new Date(b.departure_date).getTime() : 0
                                                            return dA - dB
                                                        })
                                                        .map((dep, idx) => {
                                                            const quota = dep.quota ?? null
                                                            const showLastSeats = quota !== null && quota > 0 && quota <= 5
                                                            return (
                                                                <div key={`${dep.departure_date ?? idx}-${idx}`} className="flex items-center justify-between gap-2 py-1 text-sm">
                                                                    <span className="text-alsero-dark-gray">{formatDepartureDate(dep.departure_date)}</span>
                                                                    {showLastSeats && (
                                                                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 shrink-0">
                                                                            {getVariable('tour_detail.last_seats', 'Last 5 seats')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )
                                                        })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {!hasGroupPrice && searchContextPrice && searchDepartureDate && searchPaxCount >= 1 && (
                                    <div className="rounded-lg border border-alsero-first/30 bg-alsero-first/5 p-2.5">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-alsero-dark-gray/80 mb-1">
                                            {getVariable('tour_card_price_for_selected_criteria', 'Price for selected date & travelers')}
                                        </div>
                                        <div className="flex flex-wrap items-baseline gap-1.5 text-sm">
                                            <span className="text-alsero-dark-gray">
                                                {formatDepartureDate(searchDepartureDate)}
                                                {searchPaxCount > 0 && (
                                                    <span className="text-alsero-dark-gray/80">
                                                        {isTurkish ? `, ${searchPaxCount} kişi: ` : `, ${searchPaxCount} travelers: `}
                                                    </span>
                                                )}
                                            </span>
                                            <span className="font-semibold text-alsero-first">
                                                {searchContextPrice.currency.symbol}{Number(searchContextPrice.pricePerPerson).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                {isTurkish ? '/kişi' : '/person'}
                                                {searchContextPrice.total != null && searchContextPrice.total > 0 && (
                                                    <span className="ml-1 text-alsero-dark-gray font-medium">
                                                        ({getVariable('tour_card_total', 'total')} {searchContextPrice.currency.symbol}{Number(searchContextPrice.total).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {/* Hesaplanmış fiyat yoksa base_price (en düşük kişi başı) ile "From €X" göster */}
                                {!hasGroupPrice && !hasPrivateOnlyPrice && hasBasePriceOnly && (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex gap-2 items-end flex-wrap">
                                            <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>
                                                {getVariable('tour_card_from', 'From')}
                                            </span>
                                            <span className={`${viewType == 'box' ? 'text-xl font-bold' : 'font-semibold text-lg'} text-alsero-first`}>
                                                {(tour.currency?.symbol ?? '€')}{Number(tour.base_price).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                            <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>
                                                {isTurkish ? '/kişi' : '/person'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {/* Grup fiyatı + eşleşen dönem varsa Confirm Dates (tur detayda #dates-and-availability’e scroll), yoksa Request Info/Quote */}
                                {hasGroupPrice && (
                                    <>
                                        {searchContextPrice && searchDepartureDate && searchPaxCount >= 1 && (searchContextPrice.pricePerPerson ?? 0) > 0 ? (
                                            <div className="rounded-lg border border-alsero-first/30 bg-alsero-first/5 p-2.5 mb-2">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-alsero-dark-gray/80 mb-1">
                                                    {getVariable('tour_card_price_for_selected_criteria', 'Price for selected date & travelers')}
                                                </div>
                                                <div className="flex flex-wrap items-baseline gap-1.5 text-sm">
                                                    <span className="font-semibold text-alsero-first">
                                                        {searchContextPrice.currency.symbol}{Number(searchContextPrice.pricePerPerson).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        {isTurkish ? '/kişi' : '/person'}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (() => {
                                            const groupListPrice = (tour.calculated_price?.success && (tour.calculated_price.final_price ?? 0) > 0)
                                                ? tour.calculated_price.final_price!
                                                : (tour.base_price != null && tour.base_price > 0 ? tour.base_price : null);
                                            const symbol = tour.currency?.symbol ?? '€';
                                            return (
                                                <>
                                                    {hasBothPrices && bestPrivatePrice != null ? (
                                                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2 text-sm">
                                                            {groupListPrice != null && groupListPrice > 0 && (
                                                                <span className="text-alsero-dark-gray">
                                                                    <span className="text-alsero-light-gray font-medium">{getVariable('tour_card_type_group', 'Group Tour')}:</span>{' '}
                                                                    <span className="font-semibold text-alsero-first">{symbol}{Number(groupListPrice).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                                    <span className="text-alsero-light-gray">/person</span>
                                                                </span>
                                                            )}
                                                            <span className="text-alsero-dark-gray">
                                                                <span className="text-alsero-light-gray font-medium">{getVariable('tour_card_type_private', 'Private Tour')}:</span>{' '}
                                                                <span className="font-semibold text-alsero-first">{symbol}{Number(bestPrivatePrice).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                                <span className="text-alsero-light-gray">/person</span>
                                                            </span>
                                                        </div>
                                                    ) : groupListPrice != null && groupListPrice > 0 ? (
                                                        <div className="flex gap-2 items-end flex-wrap mb-2">
                                                            <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{getVariable('tour_card_from', 'From')}</span>
                                                            <span className={`${viewType == 'box' ? 'text-xl font-bold' : 'font-semibold text-lg'} text-alsero-first`}>{symbol}{Number(groupListPrice).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                            <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{isTurkish ? '/kişi' : '/person'}</span>
                                                        </div>
                                                    ) : null}
                                                    <Link href={`${productUrl}#dates-and-availability`} className={`${viewType === 'box' ? 'block' : 'inline-block'}`}>
                                                        <button type="button" className={`${viewType == 'box' ? 'w-full py-3 px-4 rounded-lg' : 'w-auto py-2 px-5 rounded'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                                            <span>{getVariable('tour_card_check_availability', 'Check Availability')}</span>
                                                        </button>
                                                    </Link>
                                                </>
                                            );
                                        })()}
                                    </>
                                )}
                                {/* Günlük tur + fiyat (liste hesaplı fiyat yoksa): From + Rezervasyon */}
                                {hasDailyTourPeriod && !hasGroupPrice && bestPrivatePrice != null && !hasCalculatedPrice && (
                                    <>
                                        <div className="flex flex-col gap-1 mb-2">
                                            <div className="flex gap-2 items-end flex-wrap">
                                                <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{getVariable('tour_card_from', 'From')}</span>
                                                <span className={`${viewType == 'box' ? 'text-xl font-bold' : 'font-semibold text-lg'} text-alsero-first`}>
                                                    {(tour.currency?.symbol ?? '€')}{Number(bestPrivatePrice).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                                <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{isTurkish ? '/kişi' : '/person'}</span>
                                            </div>
                                        </div>
                                        <Link href={`${productUrl}#pricing`} className={`${viewType === 'box' ? 'block' : 'inline-block'}`}>
                                            <button type="button" className={`${viewType == 'box' ? 'w-full py-3 px-4 rounded-lg' : 'w-auto py-2 px-5 rounded'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                                <span>{getVariable('tour_card_reservation', isTurkish ? 'Rezervasyon' : 'Reservation')}</span>
                                            </button>
                                        </Link>
                                    </>
                                )}
                                {hasPrivateOnlyPrice && bestPrivatePrice != null && !hasDailyTourPeriod && (
                                    <>
                                        <div className="flex flex-col gap-1 mb-2">
                                            <div className="flex gap-2 items-end flex-wrap">
                                                <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{getVariable('tour_card_from', 'From')}</span>
                                                <span className={`${viewType == 'box' ? 'text-xl font-bold' : 'font-semibold text-lg'} text-alsero-first`}>
                                                    {(tour.currency?.symbol ?? '€')}{Number(bestPrivatePrice).toLocaleString(isTurkish ? 'tr-TR' : 'en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                                <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>{isTurkish ? '/kişi' : '/person'}</span>
                                            </div>
                                            <span className="text-xs text-alsero-dark-gray/80">{getVariable('tour_card_equivalent_pax', "40 pax'a denk geliyor")}</span>
                                        </div>
                                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRequestFormModalOpen(true); }} className={`${viewType == 'box' ? 'w-full py-3 px-4 rounded-lg' : 'w-auto py-2 px-5 rounded'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                            <span>{getVariable('tour_card_request_info_form', 'Request Info/Form')}</span>
                                        </button>
                                    </>
                                )}
                                {!hasGroupPrice && !hasPrivateOnlyPrice && !hasCalculatedPrice && (
                                    <Link href={productUrl} className={`${viewType === 'box' ? 'block' : 'inline-block'}`}>
                                        <button type="button" className={`${viewType == 'box' ? 'w-full py-3 px-4 rounded-lg' : 'w-auto py-2 px-5 rounded'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                            <span>{getVariable('tour_card_request_info', 'Request Info/Quote')}</span>
                                        </button>
                                    </Link>
                                )}
                                {!hasGroupPrice && hasCalculatedPrice && (!hasPrivateOnlyPrice || hasDailyTourPeriod) && (
                                    <div className="flex flex-col gap-1">
                                            {/* Fiyat gösterimi - "From" formatında (Jolly Tur formatı) */}
                                            <div className="flex flex-col gap-1">
                                                {/* Kampanya varsa üstte üstü çizili orijinal fiyat (gösterilen fiyatın orijinali) */}
                                                {tour.calculated_price && tour.calculated_price.discount_amount > 0 && (() => {
                                                        const calcPrice = tour.calculated_price!;
                                                        // Gösterilen fiyatın orijinal değerini bul
                                                        // NOT: base_price TOPLAM fiyat (pax_count × price_per_person), kişi başı değil!
                                                        // Bu yüzden base_price'ı asla göstermemeliyiz, sadece original_price_per_person veya original_single_supplement
                                                        let originalDisplayPrice: number | null = null;
                                                        
                                                        // Hangi fiyatın gösterildiğini belirle (alttaki fiyat gösterimine göre)
                                                        if (calcPrice.is_single && calcPrice.single_supplement !== null && calcPrice.single_supplement !== undefined) {
                                                            // Single supplement gösteriliyor, orijinalini bul
                                                            originalDisplayPrice = calcPrice.original_single_supplement ?? null;
                                                        } else if (calcPrice.price_per_person !== undefined && calcPrice.price_per_person > 0) {
                                                            // Price per person gösteriliyor, orijinalini bul
                                                            originalDisplayPrice = calcPrice.original_price_per_person ?? null;
                                                        }
                                                        
                                                        // Eğer orijinal gösterim fiyatı yoksa, indirimli fiyattan geriye doğru hesapla
                                                        // (discount_percentage kullanarak)
                                                        if (originalDisplayPrice === null || originalDisplayPrice === 0) {
                                                            const displayedPrice: number | null = calcPrice.is_single && calcPrice.single_supplement !== null && calcPrice.single_supplement !== undefined
                                                                ? calcPrice.single_supplement 
                                                                : (calcPrice.price_per_person !== undefined && calcPrice.price_per_person > 0 ? calcPrice.price_per_person : null);
                                                            
                                                            if (displayedPrice !== null && displayedPrice > 0 && calcPrice.discount_percentage && calcPrice.discount_percentage > 0) {
                                                                // İndirimli fiyattan orijinal fiyatı hesapla: original = discounted / (1 - discount_rate)
                                                                const discountRate = calcPrice.discount_percentage / 100;
                                                                originalDisplayPrice = displayedPrice / (1 - discountRate);
                                                            }
                                                        }
                                                        
                                                        // base_price TOPLAM fiyat olduğu için asla kullanma!
                                                        // Sadece orijinal kişi başı fiyat varsa göster
                                                        if (originalDisplayPrice === null || originalDisplayPrice === 0) {
                                                            return null; // Üstü çizili fiyat gösterme
                                                        }
                                                        
                                                        return (
                                                            <div className="flex gap-2 items-center">
                                                                <span className={`${viewType == 'box' ? 'text-base' : 'text-sm'} text-alsero-light-gray line-through`}>
                                                                    {formatPrice(originalDisplayPrice, calcPrice.currency)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}
                                                
                                                {/* Altta indirimli fiyat veya normal fiyat - Kişi başı fiyat göster */}
                                                <div className="flex gap-2 items-end flex-wrap">
                                                    <span className={`${viewType == 'box' ? 'text-sm' : 'text-xs'} text-alsero-light-gray`}>
                                                        {getVariable('tour_card_from', 'From')}
                                                    </span>
                                                    <span className={`${viewType == 'box' ? 'text-xl font-bold' : 'font-semibold text-lg'} text-alsero-first`}>
                                                        {(() => {
                                                            const calcPrice = tour.calculated_price!;
                                                            if (calcPrice.is_single && calcPrice.single_supplement !== null && calcPrice.single_supplement !== undefined) {
                                                                return formatPrice(calcPrice.single_supplement, calcPrice.currency);
                                                            }
                                                            if (calcPrice.price_per_person !== undefined && calcPrice.price_per_person > 0) {
                                                                return formatPrice(calcPrice.price_per_person, calcPrice.currency);
                                                            }
                                                            return formatPrice(calcPrice.final_price, calcPrice.currency);
                                                        })()}
                                                    </span>
                                                </div>
                                                {hasDailyTourPeriod && (
                                                    <Link href={`${productUrl}#pricing`} className={`${viewType === 'box' ? 'block mt-3' : 'inline-block mt-3'}`}>
                                                        <button type="button" className={`${viewType == 'box' ? 'w-full py-3 px-4 rounded-lg' : 'w-auto py-2 px-5 rounded'} bg-gradient-to-r from-alsero-first to-alsero-first/90 hover:from-alsero-first/90 hover:to-alsero-first/80 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center`}>
                                                            <span>{getVariable('tour_card_reservation', isTurkish ? 'Rezervasyon' : 'Reservation')}</span>
                                                        </button>
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                )}
                                
                                {/* Kampanya badge'i - Gerçek indirim yüzdesi ile */}
                                {tour.calculated_price?.success && tour.calculated_price.applied_campaigns && tour.calculated_price.applied_campaigns.length > 0 && (
                                    <div className="flex gap-1 flex-wrap">
                                        {tour.calculated_price.applied_campaigns.map((campaign) => {
                                            // Campaign name güvenli string'e çevir (çoklu dil desteği için - geriye dönük uyumluluk)
                                            let campaignName = '';
                                            if (typeof campaign.name === 'string') {
                                                campaignName = campaign.name;
                                            } else if (typeof campaign.name === 'object' && campaign.name !== null) {
                                                // Obje ise, resolvedLanguageCode'a göre çevir
                                                const langKey = resolvedLanguageCode || 'en';
                                                campaignName = (campaign.name as Record<string, string>)[langKey] 
                                                    || (campaign.name as Record<string, string>)[language || languageCode || 'en']
                                                    || Object.values(campaign.name as Record<string, string>)[0] 
                                                    || '';
                                            } else {
                                                campaignName = String(campaign.name || '');
                                            }
                                            
                                            // Gerçek indirim yüzdesini göster (kampanya bazlı veya toplam)
                                            const discountPercentage = campaign.discount_percentage 
                                                ?? tour.calculated_price?.discount_percentage 
                                                ?? (tour.calculated_price?.base_price && tour.calculated_price.base_price > 0 
                                                    ? Math.round((campaign.discount_amount / tour.calculated_price.base_price) * 100) 
                                                    : 0);
                                            
                                            // Kampanya adından mevcut yüzde bilgisini temizle (tekrar etmemek için)
                                            // Örnek: "Special Tours %12 İndirim" -> "Special Tours"
                                            const cleanCampaignName = campaignName.replace(/\s*%\d+[\d.,]*\s*%?\s*(İndirim|Discount|indirim|discount)/gi, '').trim();
                                            
                                            return (
                                                <span
                                                    key={campaign.id}
                                                    className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-alsero-first/10 text-alsero-first border border-alsero-first/20"
                                                >
                                                    {cleanCampaignName} {discountPercentage > 0 && `%${discountPercentage} ${getVariable('tour_card_discount', 'İndirim')}`}
                                                </span>
                                            );
                                        })}
                                        </div>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {(tour.brochureProductId ?? Number(tour.productId)) > 0 && (
                <BrochureDownloadModal
                    isOpen={brochureModalOpen}
                    onClose={() => setBrochureModalOpen(false)}
                    productId={(tour.brochureProductId ?? Number(tour.productId)) || 0}
                    productType="tour"
                    source="tour_card"
                    languageId={languageId ?? undefined}
                />
            )}
            {requestFormModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={() => setRequestFormModalOpen(false)} role="dialog" aria-modal="true">
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={() => setRequestFormModalOpen(false)} className="absolute top-3 right-3 p-2 rounded-lg hover:bg-gray-100 text-gray-600 z-10" aria-label="Close">×</button>
                        <div className="p-6">
                            <RequestForm
                                productId={(tour.brochureProductId ?? Number(tour.productId)) || 0}
                                productType="tour"
                                productName={tour.title}
                                language={resolvedLanguageCode ?? 'en'}
                                languageId={languageId ?? undefined}
                                initialFormDetails={null}
                            />
                        </div>
                    </div>
                </div>
            )}
    </>
    )
}
